import { Component, computed, inject, signal, TemplateRef, viewChild } from '@angular/core';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { AppState } from 'src/app/app.state';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { AccessGroup } from '../access-group.model';
import { AccessGroupCreateOrEditModalComponent } from '../access-group-create-or-edit-modal/access-group-create-or-edit-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { RequiresFeatureRoleDirective } from '../../../shared/directives/requires-feature-role.directive';
import { CreateOrEditAccessGroupSharedDiscountModalComponent } from '../../access-groups-shared-discounts/create-or-edit-access-group-shared-discount-modal/create-or-edit-access-group-shared-discount-modal.component';
import { AccessGroupStateService } from './access-group.state.service';
import { AccessGroupFilterComponent } from '../access-group-filter/access-group-filter.component';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { Organization } from '../../organization/organization-models';
import { ChargingStationLocation } from '../../charging-station-location/charging-station-location-model';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { IAccessGroupSharedDiscountSearchResultItem } from 'src/app/shared/services/access-group-shared-discount.service';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroupUsersModalComponent } from '../access-group-users-modal/access-group-users-modal.component';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import {
  SnackbarService,
  CardComponent,
  ExpandListComponent,
  ExpandListConfiguration,
  ExpandListSort,
  ScreenSizeEnum,
} from 'ok-angular-shared/components';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { DkCurrencyPipe } from '../../../shared/pipes/dk-currency';
import { TranslateAccessGroupCarTypeEnum } from '../../../shared/pipes/translate-access-group-car-type-enum.pipe';
import { MatTooltip } from '@angular/material/tooltip';
import { AuthService } from 'src/app/core/auth/auth.service';
import { Permission } from 'src/app/shared/enums/permission.enum';

@Component({
  selector: 'adm-access-group-overview',
  templateUrl: './access-group-overview.component.html',
  styleUrls: ['./access-group-overview.component.scss'],
  imports: [
    RequiresFeatureRoleDirective,
    AccessGroupFilterComponent,
    MatIcon,
    MatButton,
    CardComponent,
    ExpandListComponent,
    MatPaginator,
    AsyncPipe,
    DecimalPipe,
    RouterLink,
    DatePipe,
    DkCurrencyPipe,
    TranslateAccessGroupCarTypeEnum,
    MatTooltip,
  ],
  providers: [AccessGroupStateService],
})
export class AccessGroupOverviewComponent extends PagerBase {
  state = inject(AccessGroupStateService);
  private accessGroupService = inject(AccessGroupService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  public authService = inject(AuthService);
  private snackbarService = inject(SnackbarService);
  appState = inject(AppState);
  private dialog = inject(MatDialog);
  data = this.state.accessGroups;
  currentSort = signal<ExpandListSort<AccessGroup>>({ column: 'name', direction: 'asc' });
  organizationColumnTemplate = viewChild<TemplateRef<AccessGroup>>('organizationColumnTemplate');
  connectorCountColumnTemplate = viewChild<TemplateRef<AccessGroup>>('connectorCountColumnTemplate');
  accessCountColumnTemplate = viewChild<TemplateRef<AccessGroup>>('accessCountColumnTemplate');
  carTypeColumnTemplate = viewChild<TemplateRef<AccessGroup>>('carTypeColumnTemplate');
  amountColumnTemplate = viewChild<TemplateRef<AccessGroup>>('amountColumnTemplate');
  createdColumnTemplate = viewChild<TemplateRef<AccessGroup>>('createdColumnTemplate');
  public discountType: typeof DiscountType = DiscountType;
  public Permission = Permission;

  public roles = this.environmentLoaderService.virtualRoles;

  public config = computed<ExpandListConfiguration<AccessGroup>>(() => {
    this.state.accessGroups();

    return {
      expandDisabled: true,
      classFn: (accessGroup: AccessGroup) => {
        if (accessGroup.highlight?.getValue()) {
          return 'highlight';
        }
        return '';
      },
      columns: [
        { field: 'name', header: 'Navn', width: '1.5fr', sortField: 'name' },
        {
          field: 'organizationName',
          header: 'Organisation',
          width: '1.5fr',
          sortField: 'organizationName',
          displayTemplate: this.organizationColumnTemplate(),
        },
        {
          field: 'connectorCount',
          header: 'Ladepunkter',
          width: '1fr',
          sortField: 'connectorCount',
          displayTemplate: this.connectorCountColumnTemplate(),
        },
        {
          field: 'accessCount',
          header: 'Adgange',
          width: '1fr',
          sortField: 'accessCount',
          displayTemplate: this.accessCountColumnTemplate(),
        },
        {
          field: 'carType',
          header: 'Biltype',
          width: '1fr',
          sortField: 'carType',
          displayTemplate: this.carTypeColumnTemplate(),
        },
        {
          field: 'amount',
          header: 'Rabat pr. kWh',
          width: '1fr',
          sortField: 'amount',
          displayTemplate: this.amountColumnTemplate(),
        },
        {
          field: 'created',
          header: 'Oprettet',
          width: '1fr',
          sortField: 'created',
          visibleFrom: ScreenSizeEnum.md,
          displayTemplate: this.createdColumnTemplate(),
        },
        { field: 'createdBy', header: 'Oprettet af', width: '1fr', sortField: 'createdBy', visibleFrom: ScreenSizeEnum.md },
      ],
      actions: [
        {
          icon: { name: 'edit-s' },
          label: 'Rediger adgangsgruppe',
          action: accessGroup => accessGroup && this.editAccessGroup(accessGroup.identifier),
          hiddenFn: () => !this.authService.hasRole(this.roles[Permission.AccessGroupWrite]),
        },
        {
          svgIcon: 'security',
          label: 'Adgangsbegrænsninger for adgangsgruppe',
          action: accessGroup => accessGroup && this.editAccessGroupAccess(accessGroup),
          hiddenFn: () => !this.authService.hasRole(this.roles[Permission.AccessGroupRestrictionsRead]),
        },
        {
          icon: { name: 'trash-s' },
          label: 'Slet adgangsgruppe',
          action: accessGroup => accessGroup && this.deleteAccessGroup(accessGroup),
          hiddenFn: () => !this.authService.hasRole(this.roles[Permission.AccessGroupWrite]),
          disabledFn: accessGroup => !accessGroup || accessGroup.accessCount > 0,
        },
      ],
    };
  });

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.ToFirstPage({
        query: value && value.length > 0 ? value : null,
      });
    });
  };

  organizationSelected = (org?: Organization | null) => {
    this.state.resetLocationTrigger.set({ reset: true });
    this.state.ToFirstPage({
      organizationId: org ? org.id : null,
      locationIdentifier: null,
    });
  };

  clearOrganizationSelector() {
    this.state.resetLocationTrigger.set({ reset: true });
    this.state.ToFirstPage({
      organizationId: null,
      locationIdentifier: null,
    });
  }

  locationSelected = (location?: ChargingStationLocation | null) => {
    this.state.ToFirstPage({
      locationIdentifier: location ? location.identifier : null,
    });
  };

  discountSelected = (discount?: IAccessGroupSharedDiscountSearchResultItem | null) => {
    this.state.ToFirstPage({
      sharedDiscountIdentifier: discount ? discount.identifier : null,
    });
  };

  organizationsFetched(organizations: Organization[] | null) {
    this.state.fetchedOrganizations.set(organizations ?? []);
  }

  selectCarType(carType: AccessGroupCarType | null) {
    this.state.ToFirstPage({
      selectedCarType: carType,
    });
  }

  public resetFilter() {
    this.state.resetFilter();
  }

  public pageSwitch(event: PageEvent) {
    this.state.updatePage(event);
  }

  public onSortChange(sort: ExpandListSort<AccessGroup>) {
    this.currentSort.set(sort);

    const matSort: Sort = {
      active: sort.column as string,
      direction: sort.direction,
    };

    this.sortData(matSort);
  }

  public sortData(sort: Sort) {
    this.state.updateSort(sort);
  }

  public editAccessGroup(accessGroupIdentifier: string) {
    const dialogRef = this.dialog.open(AccessGroupCreateOrEditModalComponent, { data: { accessGroupIdentifier } });

    dialogRef.afterClosed().subscribe((accessGroup: AccessGroup) => {
      this.refreshAfterUpdate(accessGroup);
    });
  }

  public editAccessGroupAccess(accessGroup: AccessGroup) {
    const dialogRef = this.dialog.open(AccessGroupUsersModalComponent, {
      data: {
        accessGroupIdentifier: accessGroup.identifier,
        organizationId: accessGroup.organizationId,
        organizationName: this.state.fetchedOrganizations().find(organization => organization.id === accessGroup.organizationId)?.name,
      },
      width: '1024px',
    });

    dialogRef.afterClosed().subscribe((updatedAccessGroup: AccessGroup) => {
      if (updatedAccessGroup) {
        this.refreshAfterUpdate(updatedAccessGroup);
      }
    });
  }

  public deleteAccessGroup(accessGroup: AccessGroup) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Slet adgangsgruppe',
        text: 'Er du sikker på du vil slette adgangsgruppen?',
        confirmText: 'Ja',
        denyText: 'Nej',
        icon: { name: 'trash-s' },
      },
      disableClose: true,
    });

    confirmDialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.accessGroupService.deleteAccessGroup(accessGroup.identifier).subscribe({
          next: () => {
            accessGroup.isDeleted = true;
            this.refreshAfterDelete(accessGroup);
            this.snackbarService.success('Adgangsgruppen blev succesfuldt slettet.');
          },
          error: err => {
            if (err.error.code === 13004) {
              this.snackbarService.error('Adgangsgruppen har brugere tilknyttet og kan derfor ikke slettes.');
              return;
            }
            this.snackbarService.error(`Der skete fejl under sletning (${err.error.code})`);
          },
        });
      }
    });
  }

  refreshAfterUpdate(accessGroup: AccessGroup) {
    if (!accessGroup) {
      return;
    }

    this.state.triggerUpdateWithHighlight(accessGroup.identifier);
  }

  refreshAfterDelete(accessGroup: AccessGroup) {
    this.state.triggerRefresh();
    if (accessGroup.isDeleted) {
      this.state.form.patchValue({
        selectedAccessGroupIdentifier: null,
      });
    }
  }

  manageSharedDiscounts() {
    const dialogRef = this.dialog.open(CreateOrEditAccessGroupSharedDiscountModalComponent, {
      width: '800px',
    });
    dialogRef.afterClosed().subscribe((accessGroup: AccessGroup) => {
      this.state.triggerRefresh();
    });
  }

  public createOrEditAccessGroup() {
    const dialogRef = this.dialog.open(AccessGroupCreateOrEditModalComponent, {});

    dialogRef.afterClosed().subscribe((accessGroup: AccessGroup) => {
      if (accessGroup) {
        this.state.filterToAccessGroup(accessGroup.identifier);
      }
    });
  }
}
